/**
 * Created by Ning Jiang on 2023-09-05.
 */

import {ReelStopPresentationController} from "../ReelStopPresentationController";
import {ScatterSymbol} from "../../symbol/ScatterSymbol";
import {IReelStateEventData} from "../../../core/reel/ReelState";
import {StageView} from "../../view/StageView";
import {DevilsCrossRoad} from "../../DevilsCrossRoad";
import {DevilsCrossRoadSound} from "../../sound/DevilsCrossRoadSound";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {DevilsCrossRoadGameMode} from "../../server/DevilsCrossRoadGameMode";
import {DCRoadReelsController} from "../../reels/DCRoadReelsController";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";

export class ScatterLandingController extends ReelStopPresentationController {

    private _spinStates:boolean[];
    private _scatterStates:number[] = [0, 0, 0, 0];
    private _scatters:ScatterSymbol[] = [];
    private _isScatterNearWinPlaying:boolean;
    private _isLuckDrawNearWinPlaying:boolean;
    private _stopTimer:number[];

    public get scatters():ScatterSymbol[] {
        return this._scatters;
    }

    public get counter():number {
        return this._scatters.length;
    }

    public get isNearWin():boolean {
        return this._isScatterNearWinPlaying || this._isLuckDrawNearWinPlaying;
    }

    public get isLuckDrawNearWinPlaying():boolean {
        return this._isLuckDrawNearWinPlaying;
    }

    constructor() {
        super("ScatterLandingController");
    }

    public async start():Promise<any> {
        this._spinStates = [true, true, true, true];
        this._stopTimer = [0, 0, 0, 0];
        await super.start();
    }

    // override this with real animation.
    protected onReelStopped(data:IReelStateEventData):Promise<any> {
        this._spinStates[data.quadId] = false;
        if(this._spinStates.every(s => !s)) {
            this.stopAllNearWin();
        }
        return Promise.resolve();
    }

    public startNewRound():void {
        this._scatters = [];
        this._scatterStates = [0, 0, 0, 0];
    }

    public addScatter(scatter:ScatterSymbol, init:boolean):void {
        this._scatters.push(scatter);
        const devWay = DCRoadReelsController.DEV_WAYS_MAP[scatter.reelId][scatter.symId];
        if(this._scatterStates[devWay]) {
            Logger.logDev(`Error: ScatterLandingController.addScatter at ${scatter.reelId}, ${scatter.symId}. scatter already exist in quad!`);
            debugger;
        }
        this._scatterStates[devWay] = (scatter.reelId > 0 && scatter.reelId < 4 && scatter.symId > 0 && scatter.symId < 4) ? 2 : 1;

        if(init) {
            return;
        }

        this.playScatterLandingSound();
        if(this.tryStartScatterNearWin()) {
            DevilsCrossRoad.reels.tryStop(false, 0);
            return;
        }
        if(this.counter >= 4) {
            this.stopScatterNearWin();
        }
    }

    public avalancheScatter(devWay:number):void {
        this._scatterStates[devWay] = 2;
    }

    private playScatterLandingSound():void {
        const currentTime = Date.now();
        this._stopTimer.forEach((time, index) => {
            if(currentTime - time < 200) {
                DevilsCrossRoadSound.fadeAndStopSound({
                    name : DevilsCrossRoadSound.SCATTER_LAND[index],
                    to : 0,
                    duration : 20
                });
                this._stopTimer[index] = 0;
            }
        });
        DevilsCrossRoadSound.soundPlayer.playTrack({
            name : DevilsCrossRoadSound.SCATTER_LAND[this._scatters.length - 1],
            volume : 1
        });
        this._stopTimer[this._scatters.length - 1] = currentTime;
    }

    public tryStartScatterNearWin():boolean {
        if(DevilsCrossRoad.model.data.wasFeatureBuy || DevilsCrossRoad.reels.isQuickStop) {
            return false;
        }

        if(this.counter === 3 && DevilsCrossRoad.reels.reelView.hasDevWayScatterPositionSpin(this._scatterStates.findIndex(s => s===0))) {
            this._isScatterNearWinPlaying = true;
            this.startNearWin();
            return true;
        }

        return false;
    }

    public stopScatterNearWin():void {
        if(!this._isScatterNearWinPlaying) {
            return;
        }
        this._isScatterNearWinPlaying = false;
        if(!this._isScatterNearWinPlaying && !this._isLuckDrawNearWinPlaying) {
            this.stopNearWin();
            DevilsCrossRoad.reels.tryStop(DevilsCrossRoad.reels.isQuickStop, 0);
        }
    }

    public startLuckDrawNearWin():void {
        this._isLuckDrawNearWinPlaying = true;
        this.startNearWin();
    }

    public stopLuckDrawNearWin():void {
        if(!this._isLuckDrawNearWinPlaying) {
            return;
        }
        this._isLuckDrawNearWinPlaying = false;
        if(!this._isScatterNearWinPlaying && !this._isLuckDrawNearWinPlaying) {
            this.stopNearWin();
            DevilsCrossRoad.reels.tryStop(DevilsCrossRoad.reels.isQuickStop, 0);
        }
    }

    public stopAllNearWin():void {
        if(!this._isLuckDrawNearWinPlaying && !this._isScatterNearWinPlaying) {
            return;
        }
        this._isScatterNearWinPlaying = false;
        this._isLuckDrawNearWinPlaying = false;
        this.stopNearWin();
    }

    private startNearWin():void {
        DevilsCrossRoadSound.playLoopEffect(DevilsCrossRoadSound.SCATTER_ANTICIPATION);
        StageView.startNearWin();
        DevilsCrossRoad.reels.startNearWin();
    }

    private stopNearWin():void {
        DevilsCrossRoadSound.stopLoopEffect(DevilsCrossRoadSound.SCATTER_ANTICIPATION, true, 0, 100);
        if(this._scatters.length < 4) {
            if(DevilsCrossRoad.model.data.nextMode === DevilsCrossRoadGameMode.NORMAL_AVALANCHE) {
                DevilsCrossRoadSound.soundPlayer.playTrack({name : DevilsCrossRoadSound.SCATTER_ANTICIPATION_PAUSE});
            }
            if(DevilsCrossRoad.model.data.nextMode === GameMode.NORMAL) {
                DevilsCrossRoadSound.soundPlayer.playTrack({name : DevilsCrossRoadSound.SCATTER_ANTICIPATION_FAIL});
            }
        }
        StageView.stopNearWin();
        DevilsCrossRoad.reels.stopNearWin();
    }

    public skip():void {
        // Logger.logDev(`------------quick stop`);
        if(this._isScatterNearWinPlaying || this._isLuckDrawNearWinPlaying) {
            // Logger.logDev(`------------quick stop near win`);
            this.stopAllNearWin();
        }
        super.skip();
    }

    // return: 0 = no near win, 1 = normal near win, 2 = super near win.
    public getPositionNearWinState(reelId:number, symId:number):number {
        if(!this.isNearWin) {
            return 0;
        }

        const devWay = DCRoadReelsController.DEV_WAYS_MAP[reelId][symId];
        if(this._scatterStates[devWay] > 0) { // quad has scatter
            return 0;
        }

        return this._scatterStates.includes(1) ? 1 : 2;
    }
}